import { useState, useEffect, useCallback, useRef } from 'react';
import { PlusIcon, XIcon } from 'lucide-react';

interface HeaderRow {
  key: string;
  value: string;
}

interface HeadersEditorProps {
  value: Record<string, string> | null;
  onChange: (headers: Record<string, string> | null) => void;
  placeholder?: { key?: string; value?: string };
  label?: string;
  /** Shows the headers without letting them be added, edited or removed. */
  readOnly?: boolean;
}

function recordToRows(value: Record<string, string> | null): HeaderRow[] {
  if (!value || Object.keys(value).length === 0) return [];
  return Object.entries(value).map(([key, val]) => ({ key, value: val }));
}

function rowsToRecord(rows: HeaderRow[]): Record<string, string> | null {
  const filtered = rows.filter(r => r.key.trim() !== '');
  if (filtered.length === 0) return null;
  const record: Record<string, string> = {};
  for (const row of filtered) {
    record[row.key] = row.value;
  }
  return record;
}

export function HeadersEditor({
  value,
  onChange,
  placeholder,
  label,
  readOnly = false,
}: HeadersEditorProps) {
  const [rows, setRows] = useState<HeaderRow[]>(() => recordToRows(value));

  useEffect(() => {
    setRows(recordToRows(value));
  }, [value]);

  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const updateRow = (index: number, field: 'key' | 'value', val: string) => {
    setRows(prev => prev.map((r, i) => i === index ? { ...r, [field]: val } : r));
  };

  const emitChange = useCallback(() => {
    onChange(rowsToRecord(rowsRef.current));
  }, [onChange]);

  const removeRow = (index: number) => {
    const newRows = rows.filter((_, i) => i !== index);
    setRows(newRows);
    onChange(rowsToRecord(newRows));
  };

  const addRow = () => {
    setRows(prev => [...prev, { key: '', value: '' }]);
  };

  return (
    <div>
      {label && (
        <span className="text-xs text-base-content/80 block mb-1">{label}</span>
      )}
      {readOnly && rows.length === 0 && (
        <span className="text-sm text-base-content/70">None</span>
      )}
      {rows.map((row, i) => (
        <div key={i} className="flex items-center gap-2 mb-1.5">
          <input
            type="text"
            value={row.key}
            onChange={(e) => updateRow(i, 'key', e.target.value)}
            onBlur={readOnly ? undefined : emitChange}
            readOnly={readOnly}
            placeholder={placeholder?.key ?? 'Header name'}
            className="input input-sm flex-1 font-mono"
          />
          <input
            type="text"
            value={row.value}
            onChange={(e) => updateRow(i, 'value', e.target.value)}
            onBlur={readOnly ? undefined : emitChange}
            readOnly={readOnly}
            placeholder={placeholder?.value ?? 'Value'}
            className="input input-sm flex-1 font-mono"
          />
          {!readOnly && (
            <button
              type="button"
              onClick={() => removeRow(i)}
              className="btn btn-sm btn-ghost btn-square text-base-content/70 hover:text-error"
              aria-label="Remove header"
            >
              <XIcon className="size-4" />
            </button>
          )}
        </div>
      ))}
      {!readOnly && (
        <button
          type="button"
          onClick={addRow}
          className="btn btn-xs btn-ghost gap-1 text-base-content/70"
        >
          <PlusIcon className="size-3.5" />
          Add Header
        </button>
      )}
    </div>
  );
}
